import { GetProfileResponseDto } from '@shared/core/users'

/**
 * UTC midnight of the day the reminder allowance rolls over, derived from
 * `billing_cycle_day`, or null when the account has no anchor.
 *
 * Mirrors `BillingService.getNextRemindersResetDate` in the monolith, which in
 * turn mirrors reset-reminders-count.job.ts: the anchor is clamped to the
 * length of the target month, so an anchor of 29 to 31 lands on the last day of
 * a shorter month, and everything is computed in UTC because that is the clock
 * the reset cron runs on. Do not reintroduce local-time arithmetic here or the
 * date we show drifts away from the date the reset actually happens.
 */
export function getNextResetDate(profile: GetProfileResponseDto | undefined, today: Date): Date | null {
  const anchor = profile?.billing_cycle_day
  if (!anchor) {
    return null
  }

  const dayOnMonth = (year: number, month: number): number =>
    Math.min(anchor, new Date(Date.UTC(year, month + 1, 0)).getUTCDate())

  let year = today.getUTCFullYear()
  let month = today.getUTCMonth()

  // This month's reset already happened (or happens today), so the next one is
  // next month.
  if (today.getUTCDate() >= dayOnMonth(year, month)) {
    month += 1
    if (month > 11) {
      month = 0
      year += 1
    }
  }

  return new Date(Date.UTC(year, month, dayOnMonth(year, month)))
}

export function getDaysRemaining(nextResetDate: Date | null, today: Date): number {
  if (nextResetDate) {
    return Math.ceil((nextResetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  }

  // If we reached this point, return 0
  return 0
}
